import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiFolder, FiFile, FiUpload, FiDownload, FiTrash2, FiUsers, 
  FiPlus, FiChevronRight, FiHome, FiSettings, FiArrowLeft,
  FiX, FiCheck, FiRefreshCw, FiFolderPlus, FiEye, FiArchive, FiLock
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import {
  getDataRoomDetails,
  uploadFile,
  deleteFile,
  getFileDownloadUrl,
  createFolder,
  deleteFolder,
  deleteAllFolders,
  deleteAllFiles,
  formatFileSize,
  getFileIcon,
  check2FAStatus,
  DataRoomDetails,
  DataRoomFolder,
  DataRoomFile
} from '../services/dataRoomService';
import FilePreviewModal from '../components/dataroom/FilePreviewModal';

const DataRoomDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const [details, setDetails] = useState<DataRoomDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ total: number; completed: number } | null>(null);
  const [showMembersPanel, setShowMembersPanel] = useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ file: DataRoomFile; url: string } | null>(null);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [has2FA, setHas2FA] = useState<boolean | null>(null);
  const [checking2FA, setChecking2FA] = useState(true);

  useEffect(() => {
    check2FAFirst();
  }, []);

  useEffect(() => {
    if (id && has2FA === true) loadDetails();
  }, [id, has2FA]);

  const check2FAFirst = async () => {
    try {
      setChecking2FA(true);
      const enabled = await check2FAStatus();
      setHas2FA(enabled);
    } finally {
      setChecking2FA(false);
    }
  };

  const loadDetails = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await getDataRoomDetails(id);
      setDetails(data);
    } catch (err) {
      console.error('Error loading data room:', err);
      setError('Failed to load data room');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Natural sort function for folder names (handles numbers correctly: 1, 2, 3... 10, 11)
  const naturalSort = (a: DataRoomFolder, b: DataRoomFolder) => {
    return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
  };

  // Get current folder's contents (sorted)
  const currentFolders = (details?.folders.filter(f => f.parent_id === currentFolderId) || []).sort(naturalSort);
  const currentFiles = details?.files.filter(f => f.folder_id === currentFolderId) || [];

  // Breadcrumb path
  const getBreadcrumb = (): DataRoomFolder[] => {
    if (!currentFolderId || !details) return [];
    const path: DataRoomFolder[] = [];
    let folderId: string | null = currentFolderId;
    while (folderId) {
      const folder = details.folders.find(f => f.id === folderId);
      if (folder) {
        path.unshift(folder);
        folderId = folder.parent_id;
      } else {
        break;
      }
    }
    return path;
  };

  // Handle file upload
  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || !id || !details?.can_upload) return;

    setUploadProgress({ total: files.length, completed: 0 });

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const relativePath = (file as any).webkitRelativePath || '';
      
      try {
        await uploadFile(id, file, currentFolderId || undefined, relativePath);
        setUploadProgress(prev => prev ? { ...prev, completed: prev.completed + 1 } : null);
      } catch (err) {
        console.error('Error uploading file:', err);
      }
    }

    setUploadProgress(null);
    await loadDetails();
  }, [id, details?.can_upload, currentFolderId, loadDetails]);

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (details?.can_upload) setIsDragging(true);
  }, [details?.can_upload]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (!details?.can_upload || !id) return;

    // Handle both items (for folder structure) and files (fallback)
    const items = e.dataTransfer.items;
    const droppedFiles = e.dataTransfer.files;
    const files: File[] = [];
    const emptyFolderPaths: string[] = [];

    // Try webkitGetAsEntry for folder support
    let hasEntries = false;
    if (items && items.length > 0) {
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry) {
          hasEntries = true;
          break;
        }
      }
    }

    if (hasEntries) {
      const processEntry = async (entry: any, parentPath: string = ''): Promise<void> => {
        const fullPath = parentPath ? `${parentPath}/${entry.name}` : entry.name;
        
        if (entry.isFile) {
          return new Promise(resolve => {
            entry.file((file: File) => {
              Object.defineProperty(file, 'webkitRelativePath', {
                value: fullPath,
                writable: false
              });
              files.push(file);
              resolve();
            });
          });
        } else if (entry.isDirectory) {
          const reader = entry.createReader();
          return new Promise(resolve => {
            reader.readEntries(async (entries: any[]) => {
              if (entries.length === 0) {
                emptyFolderPaths.push(fullPath);
              } else {
                for (const ent of entries) {
                  await processEntry(ent, fullPath);
                }
              }
              resolve();
            });
          });
        }
      };

      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry) await processEntry(entry);
      }
    } else if (droppedFiles && droppedFiles.length > 0) {
      // Fallback: use files directly (no folder structure)
      for (let i = 0; i < droppedFiles.length; i++) {
        files.push(droppedFiles[i]);
      }
    }

    // Create empty folders first
    for (const folderPath of emptyFolderPaths) {
      const pathParts = folderPath.split('/').filter(p => p);
      let parentFolderId = currentFolderId;
      for (const folderName of pathParts) {
        try {
          const newFolder = await createFolder(id, folderName, parentFolderId || undefined);
          parentFolderId = newFolder.id;
        } catch (err) {
          console.error('Error creating empty folder:', err);
        }
      }
    }

    if (files.length > 0) {
      const dt = new DataTransfer();
      files.forEach(f => dt.items.add(f));
      await handleFileSelect(dt.files);
    } else if (emptyFolderPaths.length > 0) {
      await loadDetails();
    }
  }, [details?.can_upload, id, currentFolderId, handleFileSelect, loadDetails]);

  // Create folder
  const handleCreateFolder = async () => {
    if (!id || !newFolderName.trim()) return;
    try {
      await createFolder(id, newFolderName.trim(), currentFolderId || undefined);
      setNewFolderName('');
      setShowNewFolderModal(false);
      await loadDetails();
    } catch (err) {
      console.error('Error creating folder:', err);
    }
  };

  // Delete folder
  const handleDeleteFolder = async (folderId: string) => {
    if (!confirm('Delete this folder and all its contents?')) return;
    try {
      await deleteFolder(folderId);
      await loadDetails();
    } catch (err) {
      console.error('Error deleting folder:', err);
    }
  };

  // Download file with watermark
  const handleDownload = async (file: DataRoomFile) => {
    try {
      const { supabase } = await import('../integrations/supabase/client');
      const { data: { session } } = await supabase.auth.getSession();
      
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/download-file-watermark`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({ 
          fileId: file.id,
          storagePath: file.storage_path,
          fileName: file.file_name,
          mimeType: file.mime_type,
          dataRoomId: id
        })
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Error downloading file:', err);
    }
  };

  // Preview file
  const handlePreview = async (file: DataRoomFile) => {
    const fileExt = file.file_name?.toLowerCase().split('.').pop();
    const isImage = file.mime_type?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(fileExt || '');
    const isPdf = file.mime_type === 'application/pdf' || fileExt === 'pdf';
    const isDocx = file.mime_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || fileExt === 'docx';
    const isDoc = file.mime_type === 'application/msword' || fileExt === 'doc';
    const canPreview = isImage || isPdf || isDocx || isDoc;
    
    if (!canPreview) {
      handleDownload(file);
      return;
    }
    try {
      const url = await getFileDownloadUrl(file.storage_path);
      setPreviewFile({ file, url });
    } catch (err) {
      console.error('Error getting preview URL:', err);
    }
  };

  // Delete file
  const handleDeleteFile = async (file: DataRoomFile) => {
    if (!confirm('Delete this file?')) return;
    try {
      await deleteFile(file.id, file.storage_path);
      await loadDetails();
    } catch (err) {
      console.error('Error deleting file:', err);
    }
  };

  // Clear all folders and files (for cleanup)
  const handleClearAll = async () => {
    if (!id || !details?.can_manage) return;
    if (!confirm('Delete ALL folders and files in this data room? This cannot be undone.')) return;
    try {
      await deleteAllFiles(id);
      await deleteAllFolders(id);
      setCurrentFolderId(null);
      await loadDetails();
    } catch (err) {
      console.error('Error clearing data room:', err);
    }
  };

  // Download entire data room as ZIP
  const handleDownloadAll = async () => {
    if (!id || !details?.files?.length) return;
    
    try {
      setDownloadingZip(true);
      
      const { supabase } = await import('../integrations/supabase/client');
      const { data: { session } } = await supabase.auth.getSession();
      
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/download-dataroom-zip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({ dataRoomId: id })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create ZIP');
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${details.data_room.name.replace(/[^a-zA-Z0-9-_]/g, '_')}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
    } catch (err) {
      console.error('Error downloading ZIP:', err);
      alert('Failed to download data room. Please try again.');
    } finally {
      setDownloadingZip(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <p className="text-gray-400">Please log in to access this Data Room</p>
      </div>
    );
  }

  if (checking2FA) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Require 2FA for ALL users to access Data Room
  if (has2FA === false) {
    return (
      <div className="min-h-screen bg-gray-950 pt-20 pb-12 px-4">
        <div className="max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-900/50 rounded-2xl border border-purple-500/30 p-8 text-center"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex items-center justify-center">
              <FiLock className="w-10 h-10 text-cyan-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">
              Two-Factor Authentication Required
            </h2>
            <p className="text-gray-400 mb-6">
              For security purposes, you must enable Google Authenticator 2FA to access this Data Room.
            </p>
            <button
              onClick={() => navigate('/data-rooms/setup-2fa')}
              className="px-6 py-3 rounded-xl bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/40 text-gray-300 hover:text-white font-semibold transition-all"
            >
              Set Up 2FA Now
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !details) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error || 'Data room not found'}</p>
          <button onClick={() => navigate('/data-rooms')} className="text-cyan-400 hover:text-cyan-300">
            Back to Data Room
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 pt-20 pb-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <button
            onClick={() => {
              if (currentFolderId) {
                // Find parent folder of current folder
                const currentFolder = details?.folders.find(f => f.id === currentFolderId);
                if (currentFolder?.parent_id) {
                  // Go to parent folder
                  setCurrentFolderId(currentFolder.parent_id);
                } else {
                  // Already at top level folder, go to root
                  setCurrentFolderId(null);
                }
              } else {
                // At root, go back to data rooms list
                navigate('/data-rooms');
              }
            }}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <FiArrowLeft className="w-4 h-4" />
            {currentFolderId ? 'Back' : 'Back to Data Room'}
          </button>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <FiFolder className="text-cyan-400" />
                {details.data_room.name}
              </h1>
              <p className="text-gray-400 mt-1">
                {id === 'd6fae108-2ef9-44fd-a35b-67d438633c90' 
                  ? 'Data Room 1 - Jess Davis / Digital Trvst'
                  : id === 'd0e07e74-6ed2-4dee-b229-2db32e4ff2dd' 
                    ? 'Data Room 2 - Michael Jacobsen / Aurum Caeli Holdings S.A.'
                    : id === '52e30664-efc6-4e13-9e07-805fca6d8fb4'
                      ? 'Data Room 3 - Jeremy Gonske / Bloctech Investments'
                      : details.data_room.name}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={loadDetails}
                className="p-3 rounded-xl bg-gray-900/80 border border-purple-500/20 hover:border-purple-500/40 text-gray-300 hover:text-white transition-all"
              >
                <FiRefreshCw className="w-5 h-5" />
              </button>
              
              {/* Download All ZIP Button */}
              {details.files.length > 0 && (
                <button
                  onClick={handleDownloadAll}
                  disabled={downloadingZip}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-cyan-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-cyan-500/50 text-cyan-400 font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {downloadingZip ? (
                    <>
                      <div className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                      <span className="hidden sm:inline">Creating ZIP...</span>
                    </>
                  ) : (
                    <>
                      <FiArchive className="w-5 h-5" />
                      <span className="hidden sm:inline">Download All</span>
                    </>
                  )}
                </button>
              )}

              <button
                onClick={() => setShowMembersPanel(!showMembersPanel)}
                className="flex items-center gap-2 px-4 py-3 rounded-xl bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/40 text-gray-300 hover:text-white font-medium transition-all"
              >
                <FiUsers className="w-5 h-5 text-cyan-400" />
                <span className="hidden sm:inline">Members ({details.members.length})</span>
              </button>

              {details.can_manage && (
                <>
                  {/* Clear All Button */}
                  {(details.folders.length > 0 || details.files.length > 0) && (
                    <button
                      onClick={handleClearAll}
                      className="flex items-center gap-2 px-4 py-3 rounded-xl bg-gray-900/80 border border-red-500/30 hover:border-red-500/50 text-red-400 hover:text-red-300 font-medium transition-all"
                    >
                      <FiTrash2 className="w-5 h-5" />
                      <span className="hidden sm:inline">Clear All</span>
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/data-rooms/${id}/members`)}
                    className="p-3 rounded-xl bg-gray-900/80 border border-purple-500/20 hover:border-purple-500/40 text-gray-300 hover:text-white transition-all"
                  >
                    <FiSettings className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.div>

        <div className="flex gap-6">
          {/* Main Content */}
          <div className="flex-1">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 mb-4 text-sm">
              <button
                onClick={() => setCurrentFolderId(null)}
                className="flex items-center gap-1 text-gray-400 hover:text-cyan-400 transition-colors"
              >
                <FiHome className="w-4 h-4" />
                Root
              </button>
              {getBreadcrumb().map(folder => (
                <React.Fragment key={folder.id}>
                  <FiChevronRight className="w-4 h-4 text-gray-600" />
                  <button
                    onClick={() => setCurrentFolderId(folder.id)}
                    className="text-gray-400 hover:text-cyan-400 transition-colors"
                  >
                    {folder.name}
                  </button>
                </React.Fragment>
              ))}
            </div>

            {/* Upload Area */}
            {details.can_upload && (
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`mb-6 p-6 rounded-2xl border-2 border-dashed transition-all ${
                  isDragging
                    ? 'border-cyan-400 bg-cyan-400/5'
                    : 'border-gray-700 hover:border-gray-600'
                }`}
              >
                {uploadProgress ? (
                  <div className="text-center">
                    <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-gray-400">
                      Uploading {uploadProgress.completed} / {uploadProgress.total} files...
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <FiUpload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-400 mb-3">
                      Drag & drop files or folders here, or
                    </p>
                    <div className="flex items-center justify-center gap-3">
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="px-4 py-2.5 rounded-xl bg-gray-900/80 border border-purple-500/20 hover:border-purple-500/40 text-gray-300 hover:text-white font-medium transition-all text-sm"
                      >
                        Select Files
                      </button>
                      <button
                        onClick={() => folderInputRef.current?.click()}
                        className="px-4 py-2.5 rounded-xl bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/40 text-gray-300 hover:text-white font-medium transition-all text-sm"
                      >
                        Upload Folder
                      </button>
                      <button
                        onClick={() => setShowNewFolderModal(true)}
                        className="px-4 py-2.5 rounded-xl bg-gray-900/80 border border-purple-500/20 hover:border-purple-500/40 text-gray-300 hover:text-white font-medium transition-all text-sm flex items-center gap-2"
                      >
                        <FiFolderPlus className="w-4 h-4 text-cyan-400" />
                        New Folder
                      </button>
                    </div>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="*/*"
                  onChange={e => handleFileSelect(e.target.files)}
                  className="hidden"
                />
                <input
                  ref={folderInputRef}
                  type="file"
                  webkitdirectory=""
                  multiple
                  accept="*/*"
                  onChange={e => handleFileSelect(e.target.files)}
                  className="hidden"
                />
              </div>
            )}

            {/* Folders */}
            {currentFolders.length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Folders</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {currentFolders.map((folder, index) => (
                    <motion.div
                      key={folder.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className="group relative"
                      onClick={() => setCurrentFolderId(folder.id)}
                    >
                      <div className="p-4 rounded-xl bg-gray-900/60 border border-gray-800/60 hover:border-cyan-500/40 hover:bg-gray-900/80 cursor-pointer transition-all duration-200 h-full">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500/20 to-yellow-500/10 border border-amber-500/30 flex items-center justify-center">
                            <FiFolder className="w-5 h-5 text-amber-400" />
                          </div>
                          <div className="flex-1 min-w-0 pr-6">
                            <p className="text-white font-medium leading-snug group-hover:text-cyan-400 transition-colors break-words">
                              {folder.name}
                            </p>
                          </div>
                        </div>
                        {details.can_manage && (
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              handleDeleteFolder(folder.id);
                            }}
                            className="absolute top-3 right-3 p-1.5 rounded-md text-gray-600 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Files */}
            {currentFiles.length > 0 ? (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-3">Files</h3>
                <div className="space-y-2">
                  {currentFiles.map(file => (
                    <motion.div
                      key={file.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 rounded-xl bg-gray-900/50 border border-gray-800/50 hover:border-gray-700/50 transition-all group cursor-pointer"
                      onClick={() => handlePreview(file)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <span className="text-2xl">{getFileIcon(file.mime_type)}</span>
                          <div className="min-w-0 flex-1">
                            <p className="text-white truncate">{file.file_name}</p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(file.size_bytes)} • {file.uploader_email} • {new Date(file.uploaded_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          {(() => {
                            const ext = file.file_name?.toLowerCase().split('.').pop();
                            const showPreview = file.mime_type?.startsWith('image/') || 
                              file.mime_type === 'application/pdf' || 
                              file.mime_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
                              file.mime_type === 'application/msword' ||
                              ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext || '');
                            return showPreview ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); handlePreview(file); }}
                                className="p-2 text-gray-400 hover:text-purple-400 transition-colors"
                                title="Preview"
                              >
                                <FiEye className="w-5 h-5" />
                              </button>
                            ) : null;
                          })()}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDownload(file); }}
                            className="p-2 text-gray-400 hover:text-cyan-400 transition-colors"
                            title="Download"
                          >
                            <FiDownload className="w-5 h-5" />
                          </button>
                          {details.can_manage && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteFile(file); }}
                              className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                              title="Delete"
                            >
                              <FiTrash2 className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            ) : currentFolders.length === 0 ? (
              <div className="text-center py-12">
                <FiFile className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500">No files in this folder</p>
                {details.can_upload && (
                  <p className="text-gray-600 text-sm mt-2">
                    Drag & drop files or use the upload button
                  </p>
                )}
              </div>
            ) : null}
          </div>

          {/* Members Panel */}
          <AnimatePresence>
            {showMembersPanel && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="w-80 flex-shrink-0"
              >
                <div className="p-5 rounded-2xl bg-gray-900/50 border border-gray-800/50 sticky top-24">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <FiUsers className="text-cyan-400" />
                      Members
                    </h3>
                    <button
                      onClick={() => setShowMembersPanel(false)}
                      className="text-gray-500 hover:text-white transition-colors"
                    >
                      <FiX className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Owner */}
                  <div className="mb-4 p-3 rounded-lg bg-gray-800/50">
                    <p className="text-xs text-gray-500 mb-1">Owner</p>
                    <p className="text-white text-sm">{details.data_room.owner_email}</p>
                  </div>

                  {/* Members list */}
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {details.members.map(member => (
                      <div key={member.id} className="p-3 rounded-lg bg-gray-800/30">
                        <p className="text-white text-sm truncate">{member.email || member.name}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          member.role === 'data_room_admin' ? 'bg-cyan-500/20 text-cyan-400' :
                          member.role === 'uploader' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {member.role.replace('_', ' ')}
                        </span>
                      </div>
                    ))}
                    {details.members.length === 0 && (
                      <p className="text-gray-500 text-sm text-center py-4">
                        No additional members
                      </p>
                    )}
                  </div>

                  {details.can_manage && (
                    <button
                      onClick={() => navigate(`/data-rooms/${id}/members`)}
                      className="w-full mt-4 py-2.5 rounded-xl bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/40 text-gray-300 hover:text-white font-medium transition-all text-sm flex items-center justify-center gap-2"
                    >
                      <FiPlus className="w-4 h-4 text-cyan-400" />
                      Manage Members
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* New Folder Modal */}
      <AnimatePresence>
        {showNewFolderModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={() => setShowNewFolderModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={e => e.stopPropagation()}
              className="w-full max-w-md p-6 rounded-2xl bg-gray-900 border border-gray-800"
            >
              <h3 className="text-lg font-semibold text-white mb-4">Create New Folder</h3>
              <input
                type="text"
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                placeholder="Folder name"
                className="w-full p-3 rounded-xl bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handleCreateFolder()}
              />
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => setShowNewFolderModal(false)}
                  className="px-4 py-2 rounded-lg text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateFolder}
                  disabled={!newFolderName.trim()}
                  className="px-5 py-2.5 rounded-xl bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/40 text-gray-300 hover:text-white font-medium disabled:opacity-50 transition-all"
                >
                  Create
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={!!previewFile}
        onClose={() => setPreviewFile(null)}
        fileUrl={previewFile?.url || ''}
        fileName={previewFile?.file.file_name || ''}
        mimeType={previewFile?.file.mime_type || null}
        onDownload={() => previewFile && handleDownload(previewFile.file)}
      />

    </div>
  );
};

// Add webkitdirectory attribute type
declare module 'react' {
  interface InputHTMLAttributes<T> extends HTMLAttributes<T> {
    webkitdirectory?: string;
  }
}

export default DataRoomDetail;
